/**
 * Age Calculation Utilities
 * Functions to calculate child age from birthday, particularly for summer camp planning
 */

/**
 * Calculate age at a specific target date
 * @param {string|Date} birthday - Child's birthday (ISO date string or Date object)
 * @param {string|Date} targetDate - Target date to calculate age at (ISO date string or Date object)
 * @returns {number|null} - Age in years, or null if invalid
 */
export function calculateAgeAtDate(birthday, targetDate) {
  if (!birthday) return null;
  
  try {
    const birth = new Date(birthday);
    const target = new Date(targetDate);
    
    // Check for invalid dates
    if (isNaN(birth.getTime()) || isNaN(target.getTime())) {
      return null;
    }
    
    // Calculate age
    let age = target.getFullYear() - birth.getFullYear();
    const monthDiff = target.getMonth() - birth.getMonth();
    
    // Adjust if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && target.getDate() < birth.getDate())) {
      age--;
    }
    
    return Math.max(0, age); // Ensure non-negative
  } catch (error) {
    console.error('Error calculating age:', error);
    return null;
  }
}

/**
 * Calculate age at June 1st of the current year (summer camp age)
 * @param {string|Date} birthday - Child's birthday (ISO date string or Date object)
 * @returns {number|null} - Age in years at June 1st, or null if invalid
 */
export function calculateSummerAge(birthday) {
  if (!birthday) return null;
  
  const currentYear = new Date().getFullYear();
  const juneFirst = new Date(currentYear, 5, 1); // Month is 0-indexed, so 5 = June
  
  return calculateAgeAtDate(birthday, juneFirst);
}

/**
 * Get child age - prefers calculated age from birthday, falls back to child.age
 * @param {Object} child - Child object with optional birthday and age fields
 * @param {Date|string} [targetDate] - Optional target date (defaults to June 1st for summer age)
 * @returns {number|null} - Child's age, or null if not available
 */
export function getChildAge(child, targetDate = null) {
  if (!child) return null;
  
  // Normalize birthday: treat empty strings as null/undefined
  const birthdayValue = child.birthday;
  const hasBirthday = birthdayValue !== undefined && 
                      birthdayValue !== null && 
                      String(birthdayValue).trim() !== '';
  
  // If birthday exists and is not empty, calculate age from it
  if (hasBirthday) {
    const ageFromBirthday = targetDate 
      ? calculateAgeAtDate(birthdayValue, targetDate)
      : calculateSummerAge(birthdayValue);
    
    // Return calculated age (including 0 if that's the actual calculated age)
    // Only return null if the date is invalid
    if (ageFromBirthday !== null) {
      return ageFromBirthday;
    }
    // If birthday exists but calculation failed, don't fall back to age field
    return null;
  }
  
  // Fallback to direct age field (backward compatibility) only if birthday is not set
  if (child.age !== undefined && child.age !== null) {
    return parseInt(child.age);
  }
  
  return null;
}

/**
 * Format age for display
 * @param {number|null} age - Age in years
 * @returns {string} - Formatted age string or empty string
 */
export function formatAge(age) {
  if (age === null || age === undefined) return '';
  return `${age}`;
}

